import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, getCloudinaryUrl, Image, Radio, theme } from '@sg/garnish';
import { type IngredientModification } from '@sg/graphql-schema';

import { INGREDIENT_CARD_IMAGE_WIDTH } from '../../../../constants';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationDetailsDressingPortion = (
  props: CustomizationDetailsDressingPortionProps,
) => {
  const {
    ingredientModification,
    activeDressingsDetails,
    maxPortions,
    remainingPortions,
    setDressingPortion,
    isDisabled,
  } = props;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const { id, asset, name } = ingredientModification.ingredient;

  const formattedImageUrl = getCloudinaryUrl(asset.url, { crop: 'scale', width: INGREDIENT_CARD_IMAGE_WIDTH }); // prettier-ignore

  const selectedPortion = useMemo(() => {
    const maybeActiveDressingPortion = activeDressingsDetails.find(
      (dressing) => dressing.id === id,
    )?.portionsNumber;

    return maybeActiveDressingPortion ?? 1;
  }, [activeDressingsDetails, id]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onPortionChange = useCallback(
    (portionAsString: string) => {
      setDressingPortion(ingredientModification, Number(portionAsString));
    },
    [ingredientModification, setDressingPortion],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Image source={formattedImageUrl} style={styles.image} />

        <BodyText sizeMatch={['18']} style={styles.heading}>
          {name}
        </BodyText>
      </View>

      <Radio.Group gap={theme.spacing['2']} itemsPerRow={3}>
        {Array.from({ length: maxPortions }).map((_value, index) => {
          const portion = index + 1;
          const moreThanRemaining = portion > selectedPortion + remainingPortions;

          return (
            <Radio.Item
              key={`dressing-portion-${portion}`}
              value={`${portion}`}
              size="sm"
              isSelected={selectedPortion === portion}
              isDisabled={isDisabled || moreThanRemaining}
              onPress={onPortionChange}
              label={
                portion === 1
                  ? formatMessage(messages.labelSingular, { portion })
                  : formatMessage(messages.labelPlural, { portion })
              }
              palette="spinach"
            />
          );
        })}
      </Radio.Group>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly labelSingular: { readonly portion: MessageValue };
  readonly labelPlural: { readonly portion: MessageValue };
}>({
  labelSingular: {
    defaultMessage: '{portion} portion',
    description: 'Customization | Dressing details | Dressing portion label | Singular',
  },
  labelPlural: {
    defaultMessage: '{portion} portions',
    description: 'Customization | Dressing details | Dressing portion label | Plural',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    rowGap: theme.spacing['1'],
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['2'],
    marginBottom: theme.spacing['2'],
  },
  image: {
    width: 40,
    height: 40,
    borderRadius: 40,
  },
  heading: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationDetailsDressingPortionProps = {
  ingredientModification: IngredientModification;
  activeDressingsDetails: readonly {
    id: string;
    portionsNumber: number;
  }[];
  maxPortions: number;
  remainingPortions: number;
  setDressingPortion: (
    ingredientModification: IngredientModification,
    dressingPortion: number,
  ) => void;
  isDisabled?: boolean;
};
